import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ShieldCheck, ShieldAlert, Link2 } from "lucide-react";

function shortHash(h) {
  return h ? `${h.slice(0, 10)}…${h.slice(-8)}` : "";
}

export default function Ledger() {
  const [blocks, setBlocks] = useState([]);
  const [integrity, setIntegrity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [b, v] = await Promise.all([api.get("/ledger"), api.get("/ledger/verify")]);
        setBlocks(b.data);
        setIntegrity(v.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div data-testid="ledger-page" className="max-w-3xl mx-auto">
      <p className="overline text-[#E07A5F]">The Community Ledger</p>
      <h1 className="font-serif-dl text-4xl sm:text-5xl tracking-tight mt-2">Sealed Daps</h1>
      <p className="text-[#5C5A54] mt-3 leading-relaxed">
        Every sealed dap is written as a cryptographically linked block. Each
        block carries the hash of the one before it — change any past entry and
        the whole chain breaks.
      </p>

      {integrity && (
        <div
          data-testid="integrity-banner"
          className={`mt-6 flex items-center gap-3 rounded-lg p-4 border ${
            integrity.valid
              ? "bg-[#E6F0EB] border-[#2E6B4F]/20 text-[#2E6B4F]"
              : "bg-[#F5E6E6] border-[#9C3A3A]/20 text-[#9C3A3A]"
          }`}
        >
          {integrity.valid ? <ShieldCheck size={20} /> : <ShieldAlert size={20} />}
          <span className="text-sm font-medium">
            {integrity.valid
              ? `Chain intact — ${integrity.total_blocks} sealed dap(s) verified.`
              : `Chain tampered at block #${integrity.broken_at}.`}
          </span>
        </div>
      )}

      <div className="mt-10">
        {loading ? (
          <p className="text-[#5C5A54]">Loading…</p>
        ) : blocks.length === 0 ? (
          <div className="bg-white border border-dashed border-[#DEDBD3] rounded-lg p-12 text-center">
            <p className="font-serif-dl text-2xl text-[#5C5A54]">The ledger is empty.</p>
            <p className="text-[#5C5A54] mt-1">Sealed daps will appear here as blocks.</p>
          </div>
        ) : (
          <ol className="relative border-l-2 border-[#DEDBD3] ml-3 space-y-6">
            {blocks.map((b) => {
              const Wrapper = b.mine ? Link : "div";
              const wrapperProps = b.mine ? { to: `/dap/${b.dap_id}` } : {};
              return (
              <li key={b.index} className="ml-6" data-testid={`ledger-block-${b.index}`}>
                <span className="absolute -left-[9px] mt-2 w-4 h-4 rounded-full bg-[#1B4D3E] border-2 border-[#F9F8F6]" />
                <Wrapper
                  {...wrapperProps}
                  className={`block bg-white border border-[#DEDBD3] rounded-lg p-5 transition-shadow ${b.mine ? "hover:shadow-md" : ""}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="overline text-[#1B4D3E]">Block #{b.index}</span>
                    <div className="flex items-center gap-2">
                      {b.block_type === "resolution" ? (
                        <span
                          className={`overline px-2 py-0.5 rounded border ${
                            b.outcome === "honored"
                              ? "bg-[#E6F0EB] text-[#2E6B4F] border-[#2E6B4F]/20"
                              : "bg-[#F5E6E6] text-[#9C3A3A] border-[#9C3A3A]/20"
                          }`}
                        >
                          Resolved · {b.outcome}
                        </span>
                      ) : (
                        <span className="overline px-2 py-0.5 rounded border bg-[#EDEBF5] text-[#4B4E8C] border-[#4B4E8C]/20">
                          Sealed
                        </span>
                      )}
                      <span className="text-xs text-[#8A877F]">
                        {new Date(b.timestamp).toLocaleString()}
                      </span>
                    </div>
                  </div>
                  {b.mine ? (
                    <>
                      <p className="font-medium mt-2">{b.title}</p>
                      {b.is_demo && (
                        <span className="inline-block mt-1 overline px-1.5 py-0.5 rounded bg-[#F0EBE1] text-[#8A877F] border border-[#DEDBD3]">DEMO</span>
                      )}
                      <p className="text-sm text-[#5C5A54]">
                        {b.creator_email} ⟷ {b.counterparty_email}
                      </p>
                    </>
                  ) : (
                    <p className="font-medium mt-2 text-[#8A877F] italic" data-testid="ledger-block-private">
                      Private dap — sealed between two members
                    </p>
                  )}
                  <div className="mt-4 space-y-1 font-mono-dl text-xs text-[#8A877F]">
                    <p className="flex items-center gap-2">
                      <Link2 size={12} className="text-[#A3A095]" /> prev: {shortHash(b.prev_hash)}
                    </p>
                    <p className="text-[#1B4D3E]">hash: {shortHash(b.hash)}</p>
                  </div>
                </Wrapper>
              </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
}
